import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import InspectionUserForm from "./forms/InspectionUserForm";
import ETicketingUserForm from "./forms/ETicketingUserForm";
import ResortUserForm from "./forms/ResortUserForm";
import MainSiteUserForm from "./forms/MainSiteUserForm";

const UserForm = ({
  onCancel,
  onSave,
  user = null,
  mode = "create",
  isLoading = false,
  hotels = [],
}) => {
  const [selectedModule, setSelectedModule] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const storedUser = sessionStorage.getItem("userDetails");
  let currentUserRole = null;
  if (storedUser) {
    const userDetails = JSON.parse(storedUser);
    currentUserRole = userDetails?.role || userDetails?.roles;
  }

  // Define role hierarchy (lower number = higher privilege)
  const roleHierarchy = {
    "Super Admin": 1,
    "SuperAdmin": 1,
    "Admin": 2,
    "Manager": 3,
  };

  // Get current user's hierarchy level
  const getCurrentUserLevel = () => {
    return roleHierarchy[currentUserRole] || 999;
  };

  // Filter modules based on current user's level
  const filterModulesByHierarchy = (modules) => {
    const currentLevel = getCurrentUserLevel();
    // Only Super Admin can access "all" module
    if (currentLevel > 1) {
      return modules.filter(m => m.value !== "all");
    }
    return modules;
  };

  // Module options
  const moduleOptions = [
    { value: "e-ticketing", label: "E-Ticketing" },
    { value: "inspection", label: "Inspection App" },
    { value: "resort", label: "Resort Booking" },
    { value: "main-site", label: "Main Site" },
  ];

  useEffect(() => {
    if (user && (mode === "edit" || mode === "view")) {
      // Determine module from user data
      let userModule = "";
      
      if (user.userType === "inspection") {
        userModule = "inspection";
      } else if (user.userType === "resort") {
        userModule = "resort";
      } else if (user.userType === "mainsite") {
        userModule = "main-site";
      } else if (user.userType === "main" || user.module === "e-ticketing") {
        userModule = "e-ticketing";
      }
      
      setSelectedModule(userModule);
    } else {
      setSelectedModule("");
    }
    setValidationErrors({});
  }, [user, mode]);

  const handleModuleChange = (value) => {
    setSelectedModule(value);
    if (validationErrors.module) {
      setValidationErrors((prev) => ({
        ...prev,
        module: undefined,
      }));
    }
  };

  const getFormTitle = () => {
    switch (mode) {
      case "create":
        return "Add New User";
      case "edit":
        return "Edit User";
      case "view":
        return "View User Details";
      default:
        return "User";
    }
  };

  const isReadOnly = mode === "view";

  // Render specialized form based on selected module
  const renderModuleSpecificForm = () => {
    switch (selectedModule) {
      case "inspection":
        return (
          <InspectionUserForm
            onCancel={onCancel}
            onSave={onSave}
            user={user}
            mode={mode}
          />
        );
      case "e-ticketing":
        return (
          <ETicketingUserForm
            onCancel={onCancel}
            onSave={onSave}
            user={user}
            mode={mode}
          />
        );
      case "resort":
        return (
          <ResortUserForm
            onCancel={onCancel}
            onSave={onSave}
            user={user}
            mode={mode}
          />
        );
      case "main-site":
        return (
          <MainSiteUserForm
            onCancel={onCancel}
            onSave={onSave}
            user={user}
            mode={mode}
          />
        );
      default:
        return null;
    }
  };

  // If module is selected (or in edit/view mode), show the specialized form
  if (selectedModule && (mode === "edit" || mode === "view")) {
    return renderModuleSpecificForm();
  }

  if (selectedModule && mode === "create") {
    return renderModuleSpecificForm();
  }

  // Module selection screen (only for create mode)
  return (
    <Card className="p-4">
      <div className="d-flex align-items-center mb-4">
        <Button variant="link" className="p-0 me-3" onClick={onCancel}>
          ← Back
        </Button>
        <h4 className="m-0">{getFormTitle()}</h4>
      </div>

      <Form>
        <Row>
          <Col md={8}>
            <Form.Group className="mb-4">
              <Form.Label>
                Select Module <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={selectedModule}
                onChange={(e) => handleModuleChange(e.target.value)}
                isInvalid={!!validationErrors.module}
                disabled={isReadOnly}
                size="lg"
              >
                <option value="">Choose a module to continue...</option>
                {moduleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {validationErrors.module}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Please select the module for which you want to create a user
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default UserForm;
